/* src/components/TipModal/index.jsx */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./index.scss";
import { motion, AnimatePresence } from "framer-motion";
import CustomButton from "../CustomButton";
import tcqImage from "../../assets/images/tcq.png";

// 1) Import i18n
import { useTranslation } from "react-i18next";

const TipModal = ({ donationAmount, onClose, onTipSubmit }) => {
  const { t } = useTranslation();

  const [isSmallDonation, setIsSmallDonation] = useState(true);
  const [selectedTip, setSelectedTip] = useState(null);
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");

  useEffect(() => {
    setIsSmallDonation(donationAmount <= 500);
  }, [donationAmount]);

  // By default, these arrays have some text that might be partially localized.
  // If you want to fully localize them, define separate keys for each label.
  const smallDonationOptions = [
    { label: "$10", value: 10 },
    {
      label: `$30 ${t("tipModalRecommendedLabel")}`,
      value: 30,
      recommended: true
    },
    { label: "$50", value: 50 }
  ];

  const largeDonationOptions = [
    { label: "5%", value: 0.05 },
    {
      label: `10% ${t("tipModalRecommendedLabel")}`,
      value: 0.1,
      recommended: true
    },
    { label: "15%", value: 0.15 }
  ];

  // For the `tcq` ref case
  const tcqDonationOptions = [
    { label: "1 Box", value: 156 },
    {
      label: `3 Boxes ${t("tipModalRecommendedLabel")}`,
      value: 468,
      recommended: true
    },
    { label: "5 Boxes", value: 780 }
  ];

  let tipOptions = [];
  if (ref && ref === "tcq") {
    tipOptions = tcqDonationOptions;
  } else {
    tipOptions = isSmallDonation ? smallDonationOptions : largeDonationOptions;
  }

  const handleTipSelect = (option) => {
    setSelectedTip(option.value);
  };

  const handleSubmit = () => {
    if (selectedTip === null) {
      // If no selection made, default to recommended option
      const recommended =
        tipOptions.find((opt) => opt.recommended) || tipOptions[1];
      setSelectedTip(recommended.value);
      onTipSubmit(recommended.value);
    } else {
      onTipSubmit(selectedTip);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { opacity: 0, y: 50, transition: { duration: 0.2 } }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 }
    },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="tip-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <motion.div
          className="tip-modal-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className="tip-modal-header">
            <h2>{t("tipModalThankYou")}</h2>
            <p>{t("tipModalMakingDifference")}</p>
          </div>

          <div className="tip-modal-body">
            {ref && ref === "tcq" ? (
              <p>{t("tipModalSpreadQuran")}</p>
            ) : (
              <p>{t("tipModalLeaveTip")}</p>
            )}

            {ref && ref === "tcq" && (
              <div>
                <img src={tcqImage} alt="The Quran" width={300} />
              </div>
            )}

            <div className="tip-options">
              {tipOptions.map((option, idx) => {
                const isSelected = selectedTip === option.value;
                // For the `tcq` case, we show "- $<value>" after label
                const suffix =
                  ref && ref === "tcq" ? ` - $${option.value}` : "";
                return (
                  <button
                    key={idx}
                    className={`tip-option ${isSelected ? "recommended" : ""}`}
                    onClick={() => handleTipSelect(option)}
                  >
                    {option.label}
                    {suffix}
                  </button>
                );
              })}
            </div>

            {ref && ref === "tcq" ? (
              <p className="tip-disclaimer">{t("tipModalSponserBox")}</p>
            ) : (
              <p className="tip-disclaimer">{t("tipModalTipDisclaimer")}</p>
            )}
          </div>

          <div className="tip-modal-footer">
            <CustomButton
              text={t("tipModalConfirmButton")}
              onClick={handleSubmit}
              styleType="primary"
              disabled={!selectedTip}
            />
            <button className="no-thanks-btn" onClick={onClose}>
              {t("tipModalNoThanksButton")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TipModal;
